import { useState, useRef, useEffect, useMemo } from "react";
import { useSession } from "next-auth/client";
import {
  Flex,
  Text,
  Box,
  useColorModeValue,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from "@chakra-ui/react";
import toast from "react-hot-toast";
import { useOnClickOutside } from "utils/helpers";
import Thread from "components/tweets/masonry/thread";
import Tweet from "components/tweets/masonry/tweet";
import { TweetLabels, Label } from "components/tweet-labels";
import { getAccount } from "utils/sessionHelper";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { ViewIcon } from "components/icons/ViewIconComponent";

export function PreviewText({
  value,
  asModal,
  showThreads,
}: {
  value: any;
  asModal?: boolean;
  showThreads?: boolean;
}) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [isTweetOpen, setIsTweetOpen] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const refTweet = useRef<any>();
  const [session] = useSession();
  const tweetBg = useColorModeValue("white", "#1E1E1E");
  const isAuthenticated = useFirebaseUser();
  const miscellaneousContext: any = useMiscellaneousContext();

  useEffect(() => {
    if (session && isAuthenticated) {
      const labels = getAccount(session)?.tweetLabels ?? [];
      setLabels([...labels]);
    }
  }, [session, isAuthenticated, miscellaneousContext.refreshLabels]);

  useOnClickOutside(refTweet, async () => {
    await new Promise((resolve) => setTimeout(resolve, 200));
    setIsTweetOpen(false);
  });

  useEffect(() => {
    setSelectedLabelIds(value.tweet?.labels ?? []);
  }, []);

  const tweet = useMemo(() => {
    const _tweet = value.tweet;
    // for older posts reactivate threadTweets
    // for a time the threadTweets were not saved in the database
    if (
      value.tweet?.tweetDetails?.threadTweets?.length === 0 &&
      value.tweet?.tweetDetails?.text?.includes("\n\n\n")
    ) {
      value.tweet.tweetDetails.threadTweets =
        value.tweet?.tweetDetails?.text?.split("\n\n\n");
    }

    return _tweet;
  }, [value.tweet]);

  const onModalClose = () => {
    setIsTweetOpen(false);
  };
  const handleLabelsChange = async (labels: string[], callback) => {
    try {
      if (!isAuthenticated) {
        return;
      }

      setSelectedLabelIds(labels);
      if (value.tweet.labels) {
        value.tweet.labels = labels;
      }
      callback && callback();
      const db = firebaseClient.firestore();
      await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .collection("accounts")
        .doc(getAccount(session)?.idAccount)
        .collection("tweetsStats")
        .doc(value.tweet?.id_str)
        .update({
          labels,
        });

      const tweetDoc = await db
        .collection("users")
        .doc(getAccount(session)?.id)
        .collection("tweets")
        .where("id_str", "==", value.tweet.id_str)
        .get();

      if (!tweetDoc.empty) {
        const data = tweetDoc.docs[0].data();
        await db
          .collection("users")
          .doc(getAccount(session)?.id)
          .collection("tweets")
          .doc(data.id)
          .update({
            labels,
          });
      }
    } catch (err) {
      console.log("Error in updating stat: ", err);
      toast.error("Error in updating stat: " + err.message);
      setSelectedLabelIds(value.tweet?.labels ?? []);
    }
  };

  return (
    <Box>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        position="relative"
      >
        <Flex alignItems="center" flexDirection="row">
          <Text
            w="52"
            mr="2"
            minW="200px"
            maxW="220px"
            textAlign="start"
            noOfLines={1}
            wordBreak="break-all"
            textStyle="body.medium.light"
          >
            {value.text}
          </Text>
          <Box maxW="250px" width="100%">
            <TweetLabels
              size="xs"
              maxLabelsToDisplay={1}
              showOnlySelectedLabels={true}
              showSelectedLabelsOnSearch={true}
              mainContainerProps={{
                justify: "flex-start",
                mt: "1",
              }}
              selectedLabelIds={selectedLabelIds}
              showSelectedLabelItems={false}
              onChange={handleLabelsChange}
            />
          </Box>
        </Flex>
        <IconButton
          variant="action"
          ml="-1"
          aria-label="Preview"
          background="none"
          size="table"
          onClick={() => {
            setIsTweetOpen(true);
          }}
          icon={<ViewIcon {...{ width: 12, height: 12 }} />}
        />
        {isTweetOpen &&
          (asModal ? (
            <Modal isOpen={isTweetOpen} onClose={onModalClose}>
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Your Tweet</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  {/* 
          // @ts-ignore */}
                  <Tweet tweet={{ data: tweet }} />
                  {showThreads &&
                    tweet?.tweetDetails?.threadTweets?.length > 1 && (
                      <Box display="flex" justifyContent="center">
                        <Thread
                          // do not show the first threaded tweet, it is displayed above
                          threadTweets={tweet?.tweetDetails?.threadTweets.slice(
                            1,
                          )}
                          disableNbChar={true}
                          tweet={tweet?.tweetDetails}
                          showOutline={true}
                          disablePreview={
                            tweet?.tweetDetails?.isPreventLinkExpand
                          }
                          image={
                            tweet?.tweetDetails?.image ??
                            tweet?.tweetDetails?.profilePic
                          }
                          showGenerateImage={false}
                        />
                      </Box>
                    )}
                </ModalBody>
              </ModalContent>
            </Modal>
          ) : (
            <Box
              position="absolute"
              top="40px"
              right="0"
              minW="315px"
              bg={tweetBg}
              boxShadow="rgba(100, 100, 111, 0.15) 0px 2px 19px 0px"
              rounded="md"
              zIndex="1"
              ref={refTweet}
              fontSize="md"
              textAlign="start"
            >
              {/* 
          // @ts-ignore */}
              <Tweet tweet={{ data: value.tweet }} />
            </Box>
          ))}
      </Flex>
    </Box>
  );
}
